import { converter } from 'culori';
import { closestColor, createBrandPalette } from './brand-palette';
import { contrastRatio, deltaE, readableColor, relLum } from './contrast';

/** Paired, opaque action colors derived from an unchanged brand accent. */
export interface ButtonColors {
  accent: string;
  background: string;
  foreground: string;
  hover: string;
  hoverForeground: string;
  active: string;
  activeForeground: string;
}

/** Preserve readable brand fills; luminous accents use their computed companion. */
export function createButtonColors(value?: string | null, _isDark = false): ButtonColors | null {
  const palette = createBrandPalette(value);
  if (!palette) return null;
  const { accent, companion } = palette;
  const background = relLum(accent) >= 0.6 ? companion : readableColor(accent, '#000000', ['#ffffff']);
  const toOklch = converter('oklch');
  const lightness = toOklch(background)!.l;
  const readable = (color: string) => contrastRatio('#ffffff', color) >= 4.5;
  const hoverConstraint = (color: string) => readable(color) && deltaE(background, color) >= 8;
  const hover = closestColor(background, color => hoverConstraint(color) && toOklch(color)!.l > lightness)
    ?? closestColor(background, hoverConstraint) ?? background;
  const activeConstraint = (color: string) => readable(color) && deltaE(background, color) >= 4 && deltaE(hover, color) >= 4;
  const active = closestColor(background, color => activeConstraint(color) && toOklch(color)!.l < lightness)
    ?? closestColor(background, activeConstraint) ?? background;
  return { accent, background, foreground: '#ffffff', hover, hoverForeground: '#ffffff', active, activeForeground: '#ffffff' };
}
